import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { compact, sumBy } from 'lodash';

import { fetch } from 'later/utils/fetch';

import type Campaign from 'later/models/campaign';
import type GroupModel from 'later/models/group';
import type AuthService from 'later/services/auth';
import type ErrorsService from 'later/services/errors';

interface GetCampaignsResponse {
  meta: {
    limit: number;
    offset: number;
    totalCount: number;
  };
  data: Campaign[];
}

export default class CampaignNotificationsService extends Service {
  @service declare auth: AuthService;
  @service declare errors: ErrorsService;

  @tracked activeCount = 0;
  @tracked invitedCount = 0;

  constructor(...args: Record<string, unknown>[]) {
    super(...args);

    this.fetchInProgressCampaigns.perform();
  }

  get currentGroup(): GroupModel | undefined {
    return this.auth.currentGroup;
  }

  get totalInProgressCount(): number {
    return this.invitedCount + this.activeCount;
  }

  fetchInProgressCampaigns = task(async () => {
    try {
      if (!this.currentGroup) {
        return;
      }

      const influencerIds = compact(this.currentGroup.socialIdentities.map((si) => si.influencerId));
      const getCampaigns = async (status: 'active' | 'confirmation'): Promise<GetCampaignsResponse[]> =>
        (
          await Promise.allSettled(
            influencerIds.map((id) => fetch(`/api/v2/sapi/later-influencers/${id}/campaigns?status=${status}&limit=1`))
          )
        )
          .filter((result): result is PromiseFulfilledResult<GetCampaignsResponse> => result.status === 'fulfilled')
          .map((result) => result.value);
      const [activeResults, confirmationResults] = await Promise.all([
        getCampaigns('active'),
        getCampaigns('confirmation')
      ]);

      this.activeCount = sumBy(activeResults, (r) => r?.meta?.totalCount ?? 0);
      this.invitedCount = sumBy(confirmationResults, (r) => r?.meta?.totalCount ?? 0);
    } catch (error) {
      this.errors.log('Unable to fetch active or confirmation campaigns for influencer', {
        context: error.message,
        error
      });

      this.activeCount = 0;
      this.invitedCount = 0;
    }
  });

  updateCampaignCount = (data: { activeCount: number; invitedCount: number }): void => {
    this.activeCount = data.activeCount;
    this.invitedCount = data.invitedCount;
  };
}

declare module '@ember/service' {
  interface Registry {
    'campaign-notifications': CampaignNotificationsService;
  }
}
